<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import ButtonGroup from 'primevue/buttongroup'; // NOTE :: The PrimeVue resolver does not auto-import ButtonGroup
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import type { DialogProps } from './types';
import { useSessionStore } from '@Stores';
import { DATA_TABLE_COLUMNS_ORDER, useDataTable, useDataTableFilters } from '@DataTableModule';
import {
  type DropdownSettings,
  type FiltersProps,
  type MultiSelectSettings,
  FILTER_TYPES,
  SYSTEM_FILTERS,
  useActiveFilter,
  useSavedReports,
} from '@FilterModule';
import { ADropdown, AGroupLitters, AMultiSelect } from '../../atoms';
import { MKennelManagerFilter, MSelectInputNumber } from '../../molecules';

const emit = defineEmits<{
  onApplyFilters: [];
  onClearFilters: [];
}>();

const props = withDefaults(
  defineProps<{
    adminColumns?: string[];
    adminColumnsVisible?: string[];
    dialog?: DialogProps;
    exceptionFields?: string[];
    filters: FiltersProps[];
    hasColumns?: boolean;
    hasCustomFieldFilters?: boolean;
    loading?: boolean;
    savedReportsTableId?: string;
    showAllFilters?: boolean;
    sortByLabel?: boolean;
  }>(),
  {
    exceptionFields: () => [],
    hasColumns: false,
    hasCustomFieldFilters: false,
    showAllFilters: false,
    sortByLabel: false,
  }
);

const { shelterProfile } = storeToRefs(useSessionStore());

const { t } = useI18n();
const { _activeFilters, activeFilters } = useActiveFilter();
const { columns, frozenFields } = useDataTable();
const { activeColumns, defaultVisibleColumns, initialColumns, page, updatePaginator } = useDataTableFilters();
const { registerAvailabilityChecks, unregisterAvailabilityChecks } = useSavedReports();
// NOTE :: 100 ms after the last click. Each click restarts the timer, so a burst of quick checks scrolls once, to
// the last column checked. useTimeoutFn also stops itself when the component unmounts.
const { start: startScrollTimer, stop: stopScrollTimer } = useTimeoutFn(onScrollToCheckedFilter, 100, {
  immediate: false,
});
// NOTE :: How long a filter we just scrolled to stays highlighted.
const { start: startHighlightTimer, stop: stopHighlightTimer } = useTimeoutFn(onHighlightEnd, 900, {
  immediate: false,
});

let scrollWatchId = 0;

// NOTE :: One object for this dialog's lifetime, so unregistering can tell its own checks from another dialog's.
const AVAILABILITY_CHECKS = { isColumnAvailable, isFilterAvailable };

const LENGTH_FILTERS = ['los', 'lof', 'nof', 'nop', 'nor'];
const STANDARD_FILTER_NAMES: string[] = Object.values(SYSTEM_FILTERS);

const columnsHint = useTemplateRef<InstanceType<typeof OverlayPanel>>('columnsHint');
const filtersHint = useTemplateRef<InstanceType<typeof OverlayPanel>>('filtersHint');

const clearedFilterNames = ref<string[]>([]);
const collapsedGroups = ref<Record<string, boolean>>({
  custom: false,
  standard: false,
  withFilters: false,
  withoutFilters: false,
});
const draftColumns = ref<string[]>([]);
const highlightedFilter = ref('');
const modalVisibility = ref(false);
const pendingScrollField = ref('');
const search = ref('');

const activeFiltersCount = computed(() => props.filters.filter(({ model }) => hasModelValue(model)).length);
const dialogTitle = computed(() => (props.hasColumns ? 'filters.filtersAndColumns.title' : 'filters.filter'));
const draftFiltersCount = computed(() => _activeFilters.value.filter(({ model }) => hasModelValue(model)).length);
const frozenColumns = computed(() => [...new Set([...DATA_TABLE_COLUMNS_ORDER, ...frozenFields.value])]);
const hasActiveFilters = computed(() => activeFiltersCount.value > 0);
const hasKennelManager = computed(() => !!shelterProfile.value?.use_kennel_manager);
const searchTerm = computed(() => search.value.trim().toLowerCase());
const toggleableFields = computed(() => toggleableColumns.value.map(({ field }) => field));
const triggerTooltip = computed(() => ({ value: t(dialogTitle.value), showDelay: 1000 }));

// NOTE :: Columns the shelter admin switched off never reach "Visible Columns", so their filters are dropped too —
// the list is never filtered by a column nobody can show. showAllFilters / exceptionFields lists keep them.
const adminHiddenFields = computed(() =>
  columns.value
    .filter(({ field }) => props.adminColumns?.includes(field) && !props.adminColumnsVisible?.includes(field))
    .map(({ field }) => field)
);
// NOTE :: The filters the old Filters dialog showed, minus its "visible columns only" rule — here the column
// checkbox decides.
const availableFilters = computed(() => {
  let filters = _activeFilters.value;

  if (hasKennelManager.value) {
    filters = filters.filter((filter) => !(filter.name === 'kennel' && filter.type !== FILTER_TYPES.KENNEL_MANAGER));
  }

  if (!hasKennelManager.value) {
    filters = filters.filter((filter) => filter.type !== FILTER_TYPES.KENNEL_MANAGER);
  }

  filters = filters.filter(({ name }) => isFilterAvailable(name));

  // NOTE :: The old Filters dialog's rule for a filter that has no checkbox here: it shows only while its column is
  // on screen (a fixed column like LOS), or when the list exempts it (exceptionFields / showAllFilters). Filters of
  // pickable columns stay in the list; their checkbox decides (hiddenFilters).
  const hasVisibleColumns = draftColumns.value.length > 0;
  filters = filters.filter(
    ({ name }) =>
      !hasVisibleColumns ||
      isFilterAlwaysShown(name) ||
      (props.hasColumns && toggleableFields.value.includes(name)) ||
      draftColumns.value.includes(name)
  );

  return placeSpeciesAndBreedFirst([...filters].sort(sortFilters));
});
const columnGroups = computed(() => [
  { id: 'withFilters', label: 'filters.filtersAndColumns.columnsWithFilters', items: shownColumnsWithFilters.value },
  {
    id: 'withoutFilters',
    label: 'filters.filtersAndColumns.columnsWithoutFilters',
    items: shownColumnsWithoutFilters.value,
  },
]);
const columnsEdited = computed(() => {
  if (!props.hasColumns) return false;

  const defaultColumns = [...defaultVisibleColumns.value].sort();
  const currentColumns = [...activeColumns.value].sort();

  return JSON.stringify(defaultColumns) !== JSON.stringify(currentColumns);
});
const columnsWithFilters = computed(() =>
  toggleableColumns.value.filter(({ field }) => availableFilters.value.some((filter) => filter.name === field))
);
const columnsWithoutFilters = computed(() =>
  toggleableColumns.value.filter(({ field }) => !availableFilters.value.some((filter) => filter.name === field))
);
// NOTE :: A filter the app does not know by name (useFilters → _getUnmappedFilter) is a custom field only on
// lists whose filters come from the shelter's own forms, and those lists say so with hasCustomFieldFilters. Other
// lists build their own filters the app doesn't know by name either (Equipment Rental's "Overdue"), so they get
// one group, and the template hides its heading.
const filterGroups = computed(() => {
  if (!props.hasCustomFieldFilters) {
    const ALL_FILTERS_GROUP = {
      id: 'standard',
      label: 'filters.filtersAndColumns.standardFilters',
      items: visibleFilters.value,
    };

    return visibleFilters.value.length ? [ALL_FILTERS_GROUP] : [];
  }

  return [
    {
      id: 'standard',
      label: 'filters.filtersAndColumns.standardFilters',
      items: visibleFilters.value.filter(({ name }) => STANDARD_FILTER_NAMES.includes(name)),
    },
    {
      id: 'custom',
      label: 'filters.filtersAndColumns.customFieldFilters',
      items: visibleFilters.value.filter(({ name }) => !STANDARD_FILTER_NAMES.includes(name)),
    },
  ].filter(({ items }) => items.length);
});
const filtersPaneTitle = computed(() => {
  if (props.hasColumns) return t('filters.filtersAndColumns.filtersForVisibleColumns');

  return props.dialog?.title ?? t(dialogTitle.value);
});
const footerActionsMenu = computed(() => [
  { class: '[&_*]:!text-sky-500', command: onSelectAllColumns, icon: 'pi pi-check', label: t('common.selectAll') },
  { class: '[&_*]:!text-sky-500', command: onClearAllColumns, icon: 'pi pi-times', label: t('common.clearAll') },
  {
    class: '[&_*]:!text-sky-500',
    command: onResetColumns,
    icon: 'pi pi-refresh',
    label: t('filters.filtersAndColumns.resetColumns'),
  },
  {
    class: '[&_*]:!text-red-500',
    command: onClearDraftFilters,
    disabled: !draftFiltersCount.value,
    icon: 'pi pi-trash',
    label: t('filters.clearFilters'),
  },
]);
const hiddenFilters = computed(() =>
  availableFilters.value.filter(
    ({ name }) =>
      props.hasColumns &&
      !isFilterAlwaysShown(name) &&
      toggleableFields.value.includes(name) &&
      !draftColumns.value.includes(name)
  )
);
const looseFilters = computed(() =>
  availableFilters.value.filter((filter) => !props.hasColumns || !toggleableFields.value.includes(filter.name))
);
const shownColumnsWithFilters = computed(() =>
  columnsWithFilters.value.filter(({ header, field }) => matchesSearch(header, field))
);
const shownColumnsWithoutFilters = computed(() =>
  columnsWithoutFilters.value.filter(({ header, field }) => matchesSearch(header, field))
);
const shownLooseFilters = computed(() =>
  looseFilters.value.filter((filter) => matchesSearch(getFilterLabel(filter), filter.name))
);
const summary = computed(() => {
  const filtersText = t(
    'filters.filtersAndColumns.summaryFilters',
    { count: draftFiltersCount.value },
    draftFiltersCount.value
  );
  if (!props.hasColumns) return filtersText;

  const shownCount = toggleableFields.value.filter((field) => draftColumns.value.includes(field)).length;
  const columnsText = t('filters.filtersAndColumns.summaryColumns', {
    shown: shownCount,
    total: toggleableFields.value.length,
  });

  return `${columnsText} · ${filtersText}`;
});
// NOTE :: Admin-gated and frozen columns follow the old column picker's rules: frozen ones are never toggleable.
const toggleableColumns = computed(() => {
  const adminFields = columns.value
    .filter((column: any) => props.adminColumns?.includes(column.field))
    .map((column: any) => column.field);

  return columns.value
    .filter((column: any) => !adminFields.includes(column.field) || props.adminColumnsVisible?.includes(column.field))
    .filter(({ field }) => !frozenColumns.value.includes(field))
    .sort((first, second) => first.header.localeCompare(second.header));
});
const triggerClasses = computed(() => [
  hasActiveFilters.value
    ? 'rounded-l-md rounded-r-none border-r-0 bg-warning-300 text-white'
    : 'rounded-md bg-white text-gray-500 hover:bg-[#F3F4F6] active:bg-[#E5E7EB]',
]);
const visibleFilters = computed(() =>
  availableFilters.value
    .filter((filter) => !hiddenFilters.value.includes(filter))
    .filter((filter) => matchesSearch(getFilterLabel(filter), filter.name))
);

onMounted(onComponentMount);
onBeforeUnmount(onComponentUnmount);

// HELPERS
// NOTE :: Lists that pass showAllFilters (the bite reports) or name a filter in exceptionFields keep those filters
// on screen and set, whatever happens to their column.
function isFilterAlwaysShown(name: string) {
  return props.showAllFilters || props.exceptionFields.includes(name);
}

// NOTE :: A column the shelter admin switched off is gone for this list, and takes its filter with it unless the
// list exempts that filter. Saved reports ask the same two questions before restoring (registerAvailabilityChecks).
function isColumnAvailable(field: string) {
  return !adminHiddenFields.value.includes(field);
}

function isFilterAvailable(name: string) {
  return isFilterAlwaysShown(name) || isColumnAvailable(name);
}

function hasModelValue(model: FiltersProps['model']) {
  if (Array.isArray(model)) return !!model.length;

  return !!model;
}

function getEmptyModel(filter: FiltersProps) {
  const { DROPDOWN, GROUP_LITTERS, KENNEL_MANAGER, MULTISELECT } = FILTER_TYPES;
  const isArrayType = [MULTISELECT, KENNEL_MANAGER, GROUP_LITTERS].includes(filter.type);
  if (isArrayType) return [];

  return filter.type === DROPDOWN ? null : '';
}

function getFilterLabel(filter: FiltersProps) {
  return t(filter.text?.label || filter.name);
}

function getFilterByName(name: string) {
  return _activeFilters.value.find((filter) => filter.name === name);
}

// NOTE :: The same outline PrimeVue draws on a focused field (1px, --p-focus-ring-color), plus a short sideways
// shake, so the eye lands on the filter we scrolled to. A shake never grows the field, so the outline stays inside
// the pane. Reduced-motion users get the outline only (see the style block).
function getFilterHighlightClasses(name: string) {
  const isHighlighted = highlightedFilter.value === name;

  return [
    'w-full scroll-mb-4 scroll-mt-14 rounded-md',
    {
      'filter-shake [&_.p-inputwrapper]:outline [&_.p-inputwrapper]:outline-1 [&_.p-inputwrapper]:-outline-offset-1 [&_.p-inputwrapper]:outline-[color:var(--p-focus-ring-color)]':
        isHighlighted,
    },
  ];
}

function isFilterActive(name: string) {
  const filter = getFilterByName(name);

  return !!filter && hasModelValue(filter.model);
}

// NOTE :: Kept from the old Filters dialog: a search that matches the Breed label (in the user's language) keeps
// Species visible, since Breed needs a species first; and the kennel filter (one field that covers site, building,
// location and kennel) matches any of those words.
function matchesSearch(label: string, name: string) {
  if (!searchTerm.value) return true;
  if (label.toLowerCase().includes(searchTerm.value)) return true;

  const breedFilter = name === SYSTEM_FILTERS.species ? getFilterByName(SYSTEM_FILTERS.breed) : undefined;
  const isSpeciesForBreed = !!breedFilter && getFilterLabel(breedFilter).toLowerCase().includes(searchTerm.value);
  if (isSpeciesForBreed) return true;

  const KENNEL_SEARCH_TERMS = ['site', 'kennel', 'building', 'location', 'kennel_id'];
  const isKennelTerm =
    name === 'kennel_id' &&
    KENNEL_SEARCH_TERMS.some((term) => term.includes(searchTerm.value) || searchTerm.value.includes(term));

  return isKennelTerm;
}

// NOTE :: Species and Breed become one full-width cell (split in two inside), so they always share a row no
// matter where Species lands in the two-column grid. Every other filter is its own cell.
// NOTE :: MKennelManagerFilter renders its four fields (site, building, location, kennel) with no wrapper of its
// own, so its cell lays them out itself: one full row of four, close together, so they read as one place filter
// (Gerald's call). On narrow screens they fall back to two, then one, per row. Its own grid keeps the 28px row gap,
// or each floating label lands on the field above it. The outer grid is `grid-flow-row-dense`, so the half-width
// filters after it still fill any gap the full row leaves.
function getFilterCells(filters: FiltersProps[]) {
  const hasBreed = filters.some(({ name }) => name === SYSTEM_FILTERS.breed);
  const hasSpecies = filters.some(({ name }) => name === SYSTEM_FILTERS.species);
  const hasPair = hasBreed && hasSpecies;

  return filters
    .filter(({ name }) => !(hasPair && name === SYSTEM_FILTERS.breed))
    .map((filter) => {
      const isSpeciesPair = hasPair && filter.name === SYSTEM_FILTERS.species;
      const pairedFilters = isSpeciesPair
        ? filters.filter(({ name }) => name === SYSTEM_FILTERS.species || name === SYSTEM_FILTERS.breed)
        : [filter];
      const isFullWidth = isSpeciesPair || filter.type === FILTER_TYPES.KENNEL_MANAGER;

      return { filters: pairedFilters, isFullWidth, isPair: isSpeciesPair, key: filter.name };
    });
}

// NOTE :: Species and Breed lead the list (the PO's call: they are the filters people reach for first), and Breed
// only offers options once a species is picked, so it always follows Species.
function placeSpeciesAndBreedFirst(filters: FiltersProps[]) {
  const isSpeciesOrBreed = ({ name }: FiltersProps) => name === SYSTEM_FILTERS.species || name === SYSTEM_FILTERS.breed;
  const leading = [SYSTEM_FILTERS.species, SYSTEM_FILTERS.breed]
    .map((name) => filters.find((filter) => filter.name === name))
    .filter((filter): filter is FiltersProps => !!filter);

  return [...leading, ...filters.filter((filter) => !isSpeciesOrBreed(filter))];
}

function sortFilters(first: FiltersProps, second: FiltersProps) {
  const firstIndex = LENGTH_FILTERS.indexOf(first.name);
  const secondIndex = LENGTH_FILTERS.indexOf(second.name);
  const bothLength = firstIndex !== -1 && secondIndex !== -1;

  if (bothLength) return firstIndex - secondIndex;
  if (firstIndex !== -1) return -1;
  if (secondIndex !== -1) return 1;
  if (props.sortByLabel) return getFilterLabel(first).localeCompare(getFilterLabel(second));

  return first.name.localeCompare(second.name);
}

function componentSelector(type: FILTER_TYPES, settings?: DropdownSettings | MultiSelectSettings) {
  const { GROUP_LITTERS, KENNEL_MANAGER, MULTISELECT, SELECT_INPUT_NUMBER } = FILTER_TYPES;

  switch (type) {
    case MULTISELECT:
      return { component: AMultiSelect, defaultProps: { filter: true, optionLabel: 'name', ...settings } };
    case GROUP_LITTERS:
      return { component: AGroupLitters, defaultProps: { filter: true, optionLabel: 'name', ...settings } };
    case KENNEL_MANAGER:
      return { component: MKennelManagerFilter, defaultProps: { filter: true, optionLabel: 'name', ...settings } };
    case SELECT_INPUT_NUMBER:
      return {
        component: MSelectInputNumber,
        defaultProps: {
          dropdown: {
            checkmark: true,
            filter: true,
            highlightOnSelect: false,
            optionLabel: 'name',
            resetFilterOnHide: true,
            showClear: true,
            ...settings,
          },
          inputNumber: { min: 0 },
        },
      };
    default:
      return {
        component: ADropdown,
        defaultProps: {
          checkmark: true,
          filter: true,
          highlightOnSelect: false,
          optionLabel: 'name',
          resetFilterOnHide: true,
          showClear: true,
          ...settings,
        },
      };
  }
}

// EVENTS
// NOTE :: Only the list with saved reports hands its rules to them. A second filter bar (the shopping cart's
// product list opens on top of any page) never replaces them, and closing it never clears them.
function onComponentMount() {
  if (!props.savedReportsTableId) return;

  registerAvailabilityChecks(AVAILABILITY_CHECKS);
}

function onComponentUnmount() {
  unregisterAvailabilityChecks(AVAILABILITY_CHECKS);
}

function onOpenDialog() {
  _activeFilters.value = JSON.parse(JSON.stringify(props.filters));
  draftColumns.value = [...activeColumns.value];
  clearedFilterNames.value = [];
  search.value = '';
  modalVisibility.value = true;
}

function onCancel() {
  scrollWatchId++;
  stopScrollTimer();
  stopHighlightTimer();
  highlightedFilter.value = '';

  _activeFilters.value = JSON.parse(JSON.stringify(props.filters));
  modalVisibility.value = false;
}

// NOTE :: One Apply for both drafts: columns first, then filters, then a single refetch.
function onApply() {
  if (props.hasColumns) {
    activeColumns.value = initialColumns.value.filter((column: string) => draftColumns.value.includes(column));
  }

  activeFilters.value = _activeFilters.value;
  scrollWatchId++;
  stopScrollTimer();
  stopHighlightTimer();
  highlightedFilter.value = '';
  modalVisibility.value = false;
  page.value = 1;
  updatePaginator.value = '';
  updatePaginator.value = updatePaginator.value + 1;

  setTimeout(() => emit('onApplyFilters'), 30);
}

function onClearAppliedFilters() {
  emit('onClearFilters');

  _activeFilters.value = JSON.parse(JSON.stringify(props.filters));
}

function onClearDraftFilters() {
  _activeFilters.value.forEach((filter) => (filter.model = getEmptyModel(filter)));

  clearedFilterNames.value = [];
}

// NOTE :: A hidden column never keeps filtering the list unseen — its filter value is cleared with it.
function onToggleColumn(field: string, isChecked: boolean) {
  draftColumns.value = isChecked
    ? [...draftColumns.value, field]
    : draftColumns.value.filter((column) => column !== field);

  const filter = getFilterByName(field);
  const shouldClearFilter = !isChecked && !!filter && hasModelValue(filter.model) && !isFilterAlwaysShown(field);
  if (!shouldClearFilter || !filter) return;

  filter.model = getEmptyModel(filter);
  clearedFilterNames.value = [...clearedFilterNames.value, getFilterLabel(filter)];
}

// NOTE :: Only a single checkbox click scrolls. Select all / Clear all / Reset change many columns at once and
// call onToggleColumn directly, so they never scroll.
function onColumnCheckboxChange(field: string, isChecked: boolean) {
  onToggleColumn(field, isChecked);

  const hasFilter = availableFilters.value.some(({ name }) => name === field);
  const shouldScroll = isChecked && hasFilter;
  if (!shouldScroll) return;

  pendingScrollField.value = field;
  startScrollTimer();
}

// NOTE :: scroll-mt-14 on each filter keeps it clear of the sticky group title when scrolled to.
function scrollToFilter(name: string) {
  nextTick(() => {
    const element = document.getElementById(`filters-columns-${name}`);
    if (!element) return;

    stopHighlightTimer();
    highlightedFilter.value = '';

    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    waitForScrollToSettle(element, () => onHighlightStart(name));
  });
}

// NOTE :: The highlight waits until the filter stops moving: its position must hold for 2 frames in a row, after
// it moved at least once. A filter that needed no scroll lights up at once; a slow scroll gives up after 0.7 s.
// Watching the element (not a pane) also covers phones, where the whole dialog body is what scrolls.
// A newer click bumps scrollWatchId, which ends any older wait.
function waitForScrollToSettle(element: HTMLElement, onSettled: () => void) {
  const watchId = ++scrollWatchId;
  const startedAt = performance.now();

  let lastTop = element.getBoundingClientRect().top;
  let hasMoved = false;
  let stableFrames = 0;

  function checkPosition() {
    if (watchId !== scrollWatchId) return;

    const currentTop = element.getBoundingClientRect().top;
    const isSamePosition = Math.abs(currentTop - lastTop) < 0.5;

    hasMoved = hasMoved || !isSamePosition;
    stableFrames = isSamePosition ? stableFrames + 1 : 0;
    lastTop = currentTop;

    const elapsed = performance.now() - startedAt;
    const hasSettled = hasMoved && stableFrames >= 2;
    const neededNoScroll = !hasMoved && elapsed > 50;
    const tookTooLong = elapsed > 700;
    if (hasSettled || neededNoScroll || tookTooLong) return onSettled();

    requestAnimationFrame(checkPosition);
  }

  requestAnimationFrame(checkPosition);
}

// NOTE :: Same path as the funnel jump: a folded group opens first, then the scroll and the shake. "nearest"
// leaves the pane still when the filter is already on screen.
function onScrollToCheckedFilter() {
  const field = pendingScrollField.value;
  if (!field) return;

  pendingScrollField.value = '';

  onJumpToFilter(field);
}

function onHighlightStart(name: string) {
  highlightedFilter.value = name;
  startHighlightTimer();
}

function onHighlightEnd() {
  highlightedFilter.value = '';
}

function onSelectAllColumns() {
  const shownFields = [...shownColumnsWithFilters.value, ...shownColumnsWithoutFilters.value].map(({ field }) => field);

  draftColumns.value = [...new Set([...draftColumns.value, ...shownFields])];
}

function onClearAllColumns() {
  const shownFields = [...shownColumnsWithFilters.value, ...shownColumnsWithoutFilters.value].map(({ field }) => field);

  shownFields.forEach((field) => onToggleColumn(field, false));
}

// NOTE :: Columns the reset hides go through onToggleColumn first, so their filters clear exactly as a single
// uncheck would — otherwise Apply keeps filtering by a column that is no longer shown.
function onResetColumns() {
  toggleableFields.value
    .filter((field) => draftColumns.value.includes(field) && !defaultVisibleColumns.value.includes(field))
    .forEach((field) => onToggleColumn(field, false));

  draftColumns.value = [...defaultVisibleColumns.value];
}

// NOTE :: A folded filter group opens first, or the jump would land on a hidden field.
function onJumpToFilter(name: string) {
  const group = filterGroups.value.find(({ items }) => items.some((filter) => filter.name === name));
  if (group) collapsedGroups.value = { ...collapsedGroups.value, [group.id]: false };

  scrollToFilter(name);
}

function onToggleColumnsHint(event: Event) {
  columnsHint.value?.toggle(event);
}

function onToggleFiltersHint(event: Event) {
  filtersHint.value?.toggle(event);
}

function onToggleGroup(group: string) {
  collapsedGroups.value = { ...collapsedGroups.value, [group]: !collapsedGroups.value[group] };
}
</script>

<template>
  <!-- NOTE :: Every list with the filter bar shares this button, so the New bubble shows on each of them; hiding it
       on one list hides it everywhere. -->
  <MHelpBubble tipKey="filters-and-columns">
    <ButtonGroup class="flex flex-nowrap">
      <!-- NOTE :: The dot sits outside the Button (PrimeVue buttons clip overflow) and before it, so the theme's
           `.p-button-group .p-button:not(:last-child)` rule never strips the button's right border. -->
      <div class="relative flex">
        <i
          v-if="columnsEdited && !hasActiveFilters"
          class="pi pi-circle-fill pointer-events-none absolute right-[-6px] top-[-4px] z-10 text-sm text-warning-500"
        />

        <Button
          v-tooltip.top="triggerTooltip"
          outlined
          badgeClass="bg-warning-400 text-white text-xs"
          icon="pi pi-sliders-h"
          pt:root="h-9 border-gray-300 px-3 py-1.5 text-sm transition-all duration-200"
          severity="secondary"
          :aria-label="$t(dialogTitle)"
          :badge="hasActiveFilters ? activeFiltersCount.toString() : ''"
          :class="triggerClasses"
          :loading
          @click="onOpenDialog"
        />
      </div>

      <Transition
        enter-active-class="overflow-hidden transition-all duration-200 ease-out"
        enter-from-class="max-w-0 opacity-0"
        enter-to-class="max-w-12 opacity-100"
        leave-active-class="overflow-hidden transition-all duration-150 ease-in"
        leave-from-class="max-w-12 opacity-100"
        leave-to-class="max-w-0 opacity-0"
      >
        <Button
          v-if="hasActiveFilters"
          v-tooltip.top="$t('filters.clearFilters')"
          icon="pi pi-trash"
          pt:loadingIcon="text-2xl"
          pt:root="bg-white h-9 border-[1.5px] rounded-md px-4 border-gray-300 rounded-r-md rounded-l-none py-1.5 text-sm"
          severity="secondary"
          :aria-label="$t('filters.clearFilters')"
          :disabled="loading"
          :pt="{ icon: !loading ? 'text-red-500' : 'text-gray-500' }"
          @click="onClearAppliedFilters"
        />
      </Transition>
    </ButtonGroup>
  </MHelpBubble>

  <!-- NOTE :: A raw PrimeVue Dialog on purpose (app-07 exception, same as the old Filters dialog did): every list
       page renders its own <ADialog v-if="activeModal"> for its modals, so opening this through the shared ADialog
       would show that page's dialog too. -->
  <Dialog
    v-model:visible="modalVisibility"
    modal
    class="mx-5 w-[1060px] max-w-[calc(100vw-2.5rem)] md:h-[85vh]"
    pt:content="rounded-t-md pb-0 pt-0 md:flex md:min-h-0 md:flex-1 md:flex-col md:overflow-hidden"
    pt:footer="pt-6"
    :closableOnEscape="false"
    :showHeader="false"
  >
    <div
      :class="[
        'grid grid-cols-1 gap-6 pt-6',
        hasColumns ? 'md:min-h-0 md:flex-1 md:grid-cols-[280px_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)]' : '',
      ]"
    >
      <aside
        v-if="hasColumns"
        class="order-2 flex max-h-[55vh] flex-col border-t pt-3 md:order-1 md:h-full md:max-h-none md:border-r md:border-t-0 md:pr-4 md:pt-0"
      >
        <div class="flex flex-none flex-col gap-1 pb-3">
          <div class="hidden flex-col gap-1 md:flex">
            <MSearchInput
              v-model="search"
              customClass="w-full sm:!max-w-none"
              placeholder="filters.filtersAndColumns.search"
              :debounceTime="0"
            />

            <p class="text-xs text-slate-500">{{ summary }}</p>
          </div>

          <div class="flex items-center gap-2 md:mt-3">
            <h2 class="text-base font-semibold text-slate-700">
              {{ $t('filters.filtersAndColumns.visibleColumns') }}
            </h2>

            <Button
              rounded
              text
              class="size-6 p-0 text-slate-400"
              icon="pi pi-info-circle"
              :aria-label="$t('filters.filtersAndColumns.moreInfo')"
              @click="onToggleColumnsHint"
            />
          </div>
        </div>

        <div class="columns-scroll-hint min-h-0 flex-1 overflow-y-auto">
          <section
            v-for="group in columnGroups"
            class="mb-2"
            :key="group.id"
          >
            <Button
              text
              class="sticky top-0 z-20 flex w-full items-center justify-start gap-2 rounded-none bg-white px-1.5 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50"
              severity="secondary"
              :aria-expanded="!collapsedGroups[group.id]"
              @click="onToggleGroup(group.id)"
            >
              <i
                :class="[
                  'pi pi-chevron-down text-xs transition-transform duration-200',
                  { '-rotate-90': collapsedGroups[group.id] },
                ]"
              />

              <span>{{ $t(group.label) }}</span>

              <span class="ml-auto rounded-full bg-slate-100 px-2 text-slate-600">
                {{ group.id === 'withFilters' ? group.items.length + shownLooseFilters.length : group.items.length }}
              </span>
            </Button>

            <div
              v-show="!collapsedGroups[group.id]"
              class="flex flex-col"
            >
              <div
                v-for="{ field, header } in group.items"
                class="flex items-center justify-between gap-2 rounded-md px-1.5 py-1.5 hover:bg-slate-50"
                :key="field"
              >
                <div class="flex min-w-0 items-center gap-2 text-sm">
                  <Checkbox
                    binary
                    :inputId="`filters-columns-check-${field}`"
                    :modelValue="draftColumns.includes(field)"
                    @update:modelValue="onColumnCheckboxChange(field, $event)"
                  />

                  <label
                    class="cursor-pointer truncate"
                    :for="`filters-columns-check-${field}`"
                  >
                    {{ header }}
                  </label>
                </div>

                <Button
                  v-if="group.id === 'withFilters'"
                  v-tooltip.top="$t('filters.filtersAndColumns.goToFilter')"
                  text
                  icon="pi pi-filter-fill"
                  pt:icon="text-xs"
                  severity="secondary"
                  :aria-label="$t('filters.filtersAndColumns.goToFilter')"
                  :class="['size-6 p-0', isFilterActive(field) ? 'text-warning-500' : 'text-slate-400']"
                  @click="onJumpToFilter(field)"
                />
              </div>

              <template v-if="group.id === 'withFilters'">
                <div
                  v-for="filter in shownLooseFilters"
                  class="flex items-center justify-between gap-2 rounded-md py-1.5 pl-9 pr-1.5 text-sm hover:bg-slate-50"
                  :key="filter.name"
                >
                  <span class="truncate">{{ getFilterLabel(filter) }}</span>

                  <Button
                    text
                    icon="pi pi-filter-fill"
                    pt:icon="text-xs"
                    severity="secondary"
                    :aria-label="$t('filters.filtersAndColumns.goToFilter')"
                    :class="['size-6 p-0', isFilterActive(filter.name) ? 'text-warning-500' : 'text-slate-400']"
                    @click="onJumpToFilter(filter.name)"
                  />
                </div>
              </template>
            </div>
          </section>
        </div>
      </aside>

      <div class="order-1 flex flex-col md:order-2 md:h-full md:min-h-0">
        <div class="flex flex-none items-start justify-between gap-4">
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-semibold text-slate-700">
              {{ filtersPaneTitle }}
            </h2>

            <Button
              v-if="hasColumns"
              rounded
              text
              class="hidden size-6 p-0 text-slate-400 md:inline-flex"
              icon="pi pi-info-circle"
              :aria-label="$t('filters.filtersAndColumns.moreInfo')"
              @click="onToggleFiltersHint"
            />
          </div>

          <div class="-mr-2 -mt-2 flex items-center gap-1">
            <Button
              v-if="hasColumns"
              rounded
              text
              class="size-6 p-0 text-slate-400 md:hidden"
              icon="pi pi-info-circle"
              :aria-label="$t('filters.filtersAndColumns.moreInfo')"
              @click="onToggleFiltersHint"
            />

            <Button
              outlined
              class="border-none"
              icon="pi pi-times"
              severity="secondary"
              :aria-label="$t('common.cancel')"
              @click="onCancel"
            />
          </div>
        </div>

        <div :class="['flex flex-col gap-1 pb-2 pt-6', { 'md:hidden': hasColumns }]">
          <MSearchInput
            v-model="search"
            class="md:max-w-64"
            customClass="w-full [@media(max-width:767.98px)]:!max-w-none"
            placeholder="filters.filtersAndColumns.search"
            :debounceTime="0"
          />

          <p class="text-xs text-slate-500">{{ summary }}</p>
        </div>

        <div class="min-h-0 flex-1 md:overflow-y-auto md:pr-2">
          <template v-if="filterGroups.length">
            <section
              v-for="group in filterGroups"
              class="mb-4"
              :key="group.id"
            >
              <Button
                v-if="filterGroups.length > 1"
                text
                class="sticky top-0 z-20 flex w-full items-center justify-start gap-2 rounded-none bg-white px-1.5 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50"
                severity="secondary"
                :aria-expanded="!collapsedGroups[group.id]"
                @click="onToggleGroup(group.id)"
              >
                <i
                  :class="[
                    'pi pi-chevron-down text-xs transition-transform duration-200',
                    { '-rotate-90': collapsedGroups[group.id] },
                  ]"
                />

                <span>{{ $t(group.label) }}</span>

                <span class="ml-auto rounded-full bg-slate-100 px-2 text-slate-600">{{ group.items.length }}</span>
              </Button>

              <div
                v-show="!collapsedGroups[group.id]"
                class="grid grid-flow-row-dense grid-cols-1 gap-x-5 gap-y-7 pt-4 sm:grid-cols-2"
              >
                <div
                  v-for="cell in getFilterCells(group.items)"
                  :key="cell.key"
                  :class="[
                    'w-full',
                    { 'sm:col-span-2': cell.isFullWidth },
                    { 'grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2': cell.isPair },
                  ]"
                >
                  <div
                    v-for="filter in cell.filters"
                    :id="`filters-columns-${filter.name}`"
                    :key="filter.name"
                    :class="[
                      getFilterHighlightClasses(filter.name),
                      {
                        'grid grid-cols-1 gap-x-2 gap-y-7 sm:grid-cols-2 md:grid-cols-4':
                          filter.type === FILTER_TYPES.KENNEL_MANAGER,
                      },
                    ]"
                  >
                    <component
                      v-model="filter.model"
                      v-bind="{
                        ...componentSelector(filter.type, filter.settings).defaultProps,
                        ...filter?.text,
                        ...filter.state,
                      }"
                      :is="componentSelector(filter.type).component"
                    />
                  </div>
                </div>
              </div>
            </section>
          </template>

          <div
            v-else
            class="flex flex-col items-center justify-center gap-3 pt-5 text-center text-slate-400"
          >
            <span class="pi pi-filter-slash mb-2 text-2xl"></span>

            <p class="max-w-80">{{ $t('filters.emptyState') }}</p>
          </div>

          <div
            v-if="hiddenFilters.length"
            class="mb-4 mt-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-xs text-slate-500"
          >
            <p>
              {{ $t('filters.filtersAndColumns.hiddenHint', { count: hiddenFilters.length }, hiddenFilters.length) }}
            </p>

            <p
              v-if="clearedFilterNames.length"
              class="text-warning-500"
            >
              {{ $t('filters.filtersAndColumns.clearedHint', { names: clearedFilterNames.join(', ') }) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- NOTE :: On a phone the panel is position fixed, so it has no offset parent. PrimeVue measures
         it as hidden and leaves display none. !block puts the note back on screen. Without it the
         icon click opens nothing. -->
    <OverlayPanel
      class="max-w-80 [@media(max-width:767.98px)]:!fixed [@media(max-width:767.98px)]:!inset-x-4 [@media(max-width:767.98px)]:!top-4 [@media(max-width:767.98px)]:!block [@media(max-width:767.98px)]:max-w-none [@media(max-width:767.98px)]:before:!hidden [@media(max-width:767.98px)]:after:!hidden"
      ref="columnsHint"
    >
      <p class="text-sm text-slate-600">{{ $t('filters.filtersAndColumns.visibleColumnsHint') }}</p>
    </OverlayPanel>

    <OverlayPanel
      class="max-w-80 [@media(max-width:767.98px)]:!fixed [@media(max-width:767.98px)]:!inset-x-4 [@media(max-width:767.98px)]:!top-4 [@media(max-width:767.98px)]:!block [@media(max-width:767.98px)]:max-w-none [@media(max-width:767.98px)]:before:!hidden [@media(max-width:767.98px)]:after:!hidden"
      ref="filtersHint"
    >
      <p class="text-sm text-slate-600">{{ $t('filters.filtersAndColumns.filtersForVisibleColumnsHint') }}</p>
    </OverlayPanel>

    <template #footer>
      <div class="flex w-full flex-wrap items-center justify-between gap-3">
        <SplitButton
          v-if="hasColumns"
          outlined
          class="md:hidden"
          menuButtonIcon="pi pi-ellipsis-v"
          pt:button:root="hidden"
          pt:menu:menuItem="text-sm"
          pt:menuButton:root="af-filter-button h-9 w-9 px-4 py-1.5"
          severity="secondary"
          :menuButtonProps="{ 'aria-label': $t('filters.filtersAndColumns.moreActions') }"
          :model="footerActionsMenu"
        />

        <div :class="['-ml-4 flex-wrap gap-1', hasColumns ? 'hidden md:flex' : 'flex']">
          <template v-if="hasColumns">
            <Button
              outlined
              class="af-button-table-action"
              icon="pi pi-check"
              severity="info"
              :label="$t('common.selectAll')"
              @click="onSelectAllColumns"
            />

            <Button
              outlined
              class="af-button-table-action"
              icon="pi pi-times"
              severity="info"
              :label="$t('common.clearAll')"
              @click="onClearAllColumns"
            />

            <Button
              outlined
              class="af-button-table-action"
              icon="pi pi-refresh"
              severity="info"
              :label="$t('filters.filtersAndColumns.resetColumns')"
              @click="onResetColumns"
            />
          </template>

          <Button
            outlined
            class="af-button-table-action"
            icon="pi pi-trash"
            severity="danger"
            :disabled="!draftFiltersCount"
            :label="$t('filters.clearFilters')"
            @click="onClearDraftFilters"
          />
        </div>

        <div class="flex gap-2">
          <Button
            class="af-button"
            severity="secondary"
            :disabled="loading"
            :label="$t('common.cancel')"
            @click="onCancel"
          />

          <AButtonCheck @click="onApply" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.filter-shake {
  animation: filter-shake 0.4s ease-in-out;
}

@keyframes filter-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-4px);
  }
  40% {
    transform: translateX(4px);
  }
  60% {
    transform: translateX(-3px);
  }
  80% {
    transform: translateX(2px);
  }
}

@media (max-width: 767.98px) {
  .columns-scroll-hint {
    background:
      linear-gradient(to top, white 40%, rgb(255 255 255 / 0)) center bottom / 100% 48px no-repeat local,
      radial-gradient(farthest-side at 50% 100%, rgb(15 23 42 / 0.25), transparent) center bottom / 100% 16px no-repeat
        scroll;
  }
}

@media (prefers-reduced-motion: reduce) {
  .filter-shake {
    animation: none;
  }
}
</style>
